import { Link } from 'react-router-dom';
import usePageMeta from '../lib/usePageMeta';
import PageHero from '../components/PageHero';
import Icon from '../components/Icon';
import { SectionHead, ProcessTimeline, FaqList } from '../components/Blocks';
import { ENGAGEMENT, FAQS, WHY } from '../data';

export default function Process() {
  usePageMeta('Our Process', 'How Origo Private Limited works: discovery, design, build, launch and ongoing support — with weekly demos and transparent communication.');

  return (
    <>
      <PageHero
        eyebrow="How we work"
        title="A simple, transparent process from idea to launch"
        description="Five clear phases with a review at the end of each, so you always know where your project stands and what happens next."
        crumbs={[{ label: 'Process' }]}
      />

      <section className="section bg-white">
        <div className="container-x grid gap-14 lg:grid-cols-[.85fr_1.15fr]">
          <div className="lg:sticky lg:top-32 lg:self-start" data-anim="left">
            <span className="eyebrow">Our workflow</span>
            <h2 className="h2 mt-5 text-ink">What to <span className="text-accent">expect</span> when you work <span className="text-outline">with us</span></h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              We run every project in short, visible iterations. You get a dedicated project manager, a shared workspace and a demo every week — no black boxes.
            </p>
            <ul className="mt-6 space-y-3">
              {['Dedicated project manager', 'Weekly demos and written updates', 'Shared task board & staging site', 'Direct access to the team on Slack'].map((t) => (
                <li key={t} className="flex gap-3 text-slate-700"><Icon name="check" className="mt-0.5 h-5 w-5 shrink-0 text-brand" strokeWidth={2.6} />{t}</li>
              ))}
            </ul>
          </div>
          <ProcessTimeline />
        </div>
      </section>

      <section className="section bg-ink text-white">
        <div className="grid-bg pointer-events-none absolute inset-0" />
        <div className="container-x relative">
          <SectionHead dark eyebrow="Our promise" title="What you can count on" />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-anim="up" data-stagger>
            {WHY.map((w) => (
              <div key={w.title} className="lift rounded-3xl border border-white/10 bg-white/[0.04] p-7">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-lime/15 text-brand-lime"><Icon name={w.icon} /></span>
                <h3 className="mt-5 text-lg font-bold">{w.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-white/65">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-brand-soft">
        <div className="container-x">
          <SectionHead eyebrow="Engagement models" title="Pick the model that fits your project" />
          <div className="mt-14 grid gap-6 lg:grid-cols-3" data-anim="flip" data-stagger>
            {ENGAGEMENT.map((m) => (
              <div key={m.title} className={`lift rounded-3xl p-8 ${m.highlight ? 'bg-gradient-to-br from-brand to-ink text-white shadow-2xl' : 'bg-white ring-1 ring-brand/10'}`}>
                <h3 className="text-2xl font-bold">{m.title}</h3>
                <p className={`mt-3 ${m.highlight ? 'text-white/75' : 'text-slate-600'}`}>{m.desc}</p>
                <ul className="mt-6 space-y-3">
                  {m.points.map((p) => <li key={p} className="flex items-center gap-2 text-sm"><Icon name="check" className={`h-4 w-4 ${m.highlight ? 'text-brand-lime' : 'text-brand'}`} strokeWidth={2.6} />{p}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x grid gap-14 lg:grid-cols-[.8fr_1.2fr]">
          <div data-anim="left">
            <span className="eyebrow">FAQ</span>
            <h2 className="h2 mt-5 text-ink">Questions about <span className="text-accent">working</span> <span className="text-outline">together</span></h2>
            <Link to="/contact" className="btn-green mt-8">Start a project <Icon name="arrow" className="h-4 w-4" /></Link>
          </div>
          <FaqList items={FAQS} />
        </div>
      </section>
    </>
  );
}
